import React, { FC, memo, ReactNode } from 'react'

import { DevTool } from '@hookform/devtools'
import { FormProvider } from 'react-hook-form'

import { SignedUpCustomer, useCustomerSignUp } from 'Hooks/useCustomerSignUp'
import { Customer } from 'Types'

import Code from './Code'
import Identify from './Identify'
import LoggedCustomerView from './LoggedCustomerView'
import Phone from './Phone'
import ResendButton from './ResendButton'
import VerifiedBadge from './VerifiedBadge'

export type { SignedUpCustomer }

export interface CustomerFieldsGroupProps {
  customer?: Customer | null
  isActive?: boolean
  onChangeStep?: () => void
  onComplete?: (customer: SignedUpCustomer) => void
  renderSubmitButton?: (submit: () => void, submitting: boolean, isPhoneStep: boolean) => ReactNode
  submitting?: boolean
}

const CustomerFieldsGroup: FC<CustomerFieldsGroupProps> = ({
  isActive = true,
  onChangeStep,
  onComplete = () => {},
  renderSubmitButton,
  submitting = false,
  customer,
}) => {
  const {
    codeInputRef,
    form,
    handleLogout,
    handleResend,
    onSubmit,
    resendSecondsLeft,
    showDetails,
    signing,
    state,
    stopResendCooldown,
    twoFactorAuth,
  } = useCustomerSignUp(customer, { onLoggedOut: onChangeStep, onSignedUp: onComplete })

  const phoneSectionDescription =
    state.isPhone || state.isMultiple
      ? 'We use this number to send a verification code for checkout'
      : state.isCode
        ? 'We have sent a 6-digit authorization code to your number'
        : 'Phone number is successfully verified'

  return (
    <FormProvider {...form}>
      <DevTool control={form.control} />
      <div className="checkout-step / mb-5">
        <h2 className="checkout-step__header / h4">
          <span>Contact</span>

          {isActive ? (
            state.isPhone ? null : (
              <button className="checkout-button / ml-auto" type="button" onClick={handleLogout}>
                Log out
              </button>
            )
          ) : onChangeStep ? (
            <button className="checkout-button / ml-auto" type="button" onClick={onChangeStep}>
              Change
            </button>
          ) : null}
        </h2>

        <form
          className="checkout-contact-form"
          hidden={!isActive || customer?.signedUp}
          onSubmit={(e) => void onSubmit(e)}
        >
          {state.isCode ? (
            <p aria-live="polite" className="sr-only" role="status">
              Enter the 6-digit verification code to continue checkout.
            </p>
          ) : null}

          <div className="checkout-contact-flow">
            {customer?.signedUp ? null : (
              <section className="checkout-contact-flow__panel checkout-contact-flow__panel--phone">
                {twoFactorAuth ? (
                  <>
                    <div className="checkout-contact-flow__title-row">
                      <h3 className="checkout-contact-flow__title">Verify Phone</h3>
                      {state.isCode || state.isPhone ? null : <VerifiedBadge />}
                    </div>
                    <p className="checkout-contact-flow__description mb-1">{phoneSectionDescription}</p>
                  </>
                ) : null}
                <Phone disabled={!state.isPhone} submitting={signing || submitting} />
                {/* Stays mounted (like Phone/Details' fields) even once past the code
                    step — react-hook-form's useController force-unregisters a field
                    when its component unmounts regardless of shouldUnregister, so
                    conditionally unrendering Code here was dropping `code` from the
                    form entirely once the flow advanced, and the next step's submit
                    (e.g. password) went out without it. Visibility stays controlled
                    by the wrapper's `hidden` attribute only. */}
                <div aria-hidden={!state.isCode} className="checkout-contact-flow__code-reveal" hidden={!state.isCode}>
                  <Code ref={codeInputRef} canAutoSubmit={!(signing || submitting)} onSubmit={() => void onSubmit()}>
                    <ResendButton duration={resendSecondsLeft} onResend={handleResend} onStop={stopResendCooldown} />
                  </Code>
                </div>
              </section>
            )}

            {showDetails || state.isPassword ? <Identify showDetails={showDetails} state={state} /> : null}
          </div>
        </form>
        {customer?.signedUp ? (
          <LoggedCustomerView email={customer.email} firstName={customer.firstName} lastName={customer.lastName} />
        ) : null}
      </div>

      {isActive && renderSubmitButton
        ? renderSubmitButton(() => void onSubmit(), signing || submitting, state.isPhone)
        : null}
    </FormProvider>
  )
}

export default memo(CustomerFieldsGroup)
